// P2P trade dialog — two-pane modal: your offer | their offer + coin input.
// Atomic swap fires when both sides confirm. Built on top of existing server
// trade service (TradeService). Opens automatically when a trade_window message
// arrives; also callable via openTradePanel() for testing.

import type { Net } from "../net.js";
import type { ClientStore, ClientState, TradeSide, TradeView } from "../state.js";

export function openTradePanel(host: HTMLElement, net: Net, store: ClientStore): HTMLElement {
  const existing = host.querySelector<HTMLElement>(".trade-overlay");
  if (existing) return existing;

  const overlay = document.createElement("div");
  overlay.className = "trade-overlay";
  host.appendChild(overlay);

  function render(s: ClientState): void {
    const t = s.trade;
    if (!t) {
      overlay.remove();
      return;
    }
    const yourReady = t.you.confirmed;
    const theirReady = t.them.confirmed;
    overlay.innerHTML = `
      <div class="trade-modal" role="dialog" aria-label="Trade">
        <header class="trade-header">
          <h2>Trading with ${escape(t.them.name)}</h2>
          <button id="trade-cancel" class="trade-cancel" aria-label="Cancel trade">×</button>
        </header>
        <section class="trade-panes">
          ${renderSide(t.you, "you", "Your offer")}
          ${renderSide(t.them, "them", escape(t.them.name) + "'s offer")}
        </section>
        <section class="trade-gold">
          <label>Your gold offer:
            <input id="trade-gold-input" type="number" min="0" step="1" value="${t.you.gold}" />
          </label>
          <button id="trade-gold-update" class="trade-action">Set gold</button>
        </section>
        <section class="trade-actions">
          <button id="trade-add-from-inv" class="trade-action">Add item from inventory…</button>
          <button id="trade-confirm" class="trade-action ${yourReady ? "confirmed" : "primary"}">${yourReady ? "✓ Confirmed (waiting)" : "Ready"}</button>
          <button id="trade-cancel-2" class="trade-action danger">Cancel trade</button>
        </section>
        <footer class="trade-footer dim">
          ${theirReady ? "They're ready. " : ""}
          ${yourReady && theirReady ? "Swapping..." : "Trade completes when both sides confirm."}
        </footer>
      </div>
    `;

    overlay.querySelector<HTMLButtonElement>("#trade-confirm")?.addEventListener("click", () => net.send({ t: "trade_confirm" }));
    overlay.querySelector<HTMLButtonElement>("#trade-cancel")?.addEventListener("click", () => net.send({ t: "trade_cancel" }));
    overlay.querySelector<HTMLButtonElement>("#trade-cancel-2")?.addEventListener("click", () => net.send({ t: "trade_cancel" }));
    overlay.querySelector<HTMLButtonElement>("#trade-gold-update")?.addEventListener("click", () => {
      const input = overlay.querySelector<HTMLInputElement>("#trade-gold-input")!;
      const gold = Math.max(0, Math.floor(Number(input.value) || 0));
      net.send({ t: "trade_offer_gold", trade_id: t.trade_id, gold });
    });
    overlay.querySelector<HTMLButtonElement>("#trade-add-from-inv")?.addEventListener("click", () => {
      // Prompt for instance_id until we have a proper inventory-picker modal.
      const id = window.prompt("Item instance_id to offer (check inventory panel):");
      if (!id) return;
      const instanceId = Number.parseInt(id, 10);
      if (!Number.isFinite(instanceId)) return;
      net.send({ t: "trade_offer_item", trade_id: t.trade_id, instance_id: instanceId });
    });
  }

  function renderSide(side: TradeSide, who: "you" | "them", label: string): string {
    return `
      <div class="trade-pane trade-pane-${who} ${side.confirmed ? "confirmed" : ""}">
        <h3>${label}${side.confirmed ? ' <span class="trade-ready">✓</span>' : ""}</h3>
        ${side.items.length === 0
          ? `<p class="dim">No items.</p>`
          : `<ul class="trade-items">${side.items.map((i) => `
              <li>${escape(i.name)}${i.qty > 1 ? ` ×${i.qty}` : ""}</li>
            `).join("")}</ul>`}
        <div class="trade-gold-display">${side.gold} gold</div>
      </div>
    `;
  }

  const unsubscribe = store.subscribe(render);
  render(store.get());
  return overlay;
}

function escape(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" }[c]!));
}

/** Auto-mount: show the panel whenever a trade session exists in state. */
export function mountTradePanelAutoShow(host: HTMLElement, net: Net, store: ClientStore): void {
  let current: HTMLElement | null = null;
  store.subscribe((s) => {
    if (s.trade && !current) current = openTradePanel(host, net, store);
    else if (!s.trade && current) { current.remove(); current = null; }
  });
}
